import React, { useState } from 'react';
import { 
    Box, 
    Button, 
    Container, 
    Typography, 
    useTheme, 
    IconButton, 
    Drawer, 
    List, 
    ListItem, 
    ListItemButton, 
    ListItemText,
    useMediaQuery 
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navLinkStyle = {
        fontSize: { lg: '14px', md: '14px', sm: '13px' },
        cursor: 'pointer',
        fontWeight: 500,
        '&:hover': { color: theme.palette.primary.main }
    };

    const navItems = [
        { label: 'Home', path: '/', isActive: location?.pathname === '/' },
        { label: 'Events', path: '/events', isActive: location?.pathname?.startsWith('/event') },
        { label: 'Press & Media', path: '/press-and-media', isActive: location?.pathname === '/press-and-media' },
        { label: 'About Us', path: '/about-us', isActive: location?.pathname === '/about-us' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            {/* Top Bar - Hidden on mobile */}
            <Box sx={{ backgroundColor: theme.palette.primary.main, display: { xs: 'none', sm: 'block' } }}>
                <Container>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { lg: "24px", md: '24px', sm: '24px', xs: '12px' }, justifyContent: { lg: "space-between", md: "space-between", sm: "center", xs: "center" }, padding: '10px 0' }}>
                        <Typography sx={{ color: theme.palette.bgLightWhite?.main || '#fff', fontSize: '14px', lineHeight: '20px' }}>Patidar Parivar Suraksha Kavach</Typography>
                        <Box sx={{ display: 'flex', gap: { lg: "24px", md: '24px', sm: '20px', xs: '12px' }, flexWrap: 'wrap', justifyContent: { lg: "space-between", md: "space-between", sm: "center", xs: "center" } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <img
                                    src="/images/call.svg"
                                    alt="phone"
                                    style={{ width: "20px", objectFit: "cover", filter: "invert(100%) sepia(100%) saturate(0%) hue-rotate(73deg) brightness(103%) contrast(103%)" }}
                                />
                                <Typography sx={{ color: theme.palette.bgLightWhite?.main || '#fff', fontSize: '14px', lineHeight: '20px', fontWeight: '400' }}> +91 98498 76476 | +91 99499 21021</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <img
                                    src="/images/sms-tracking.svg"
                                    alt="email"
                                    style={{ width: "20px", objectFit: "cover", filter: "invert(100%) sepia(100%) saturate(0%) hue-rotate(73deg) brightness(103%) contrast(103%)" }}
                                />
                                <Typography sx={{ color: theme.palette.bgLightWhite?.main || '#fff', fontSize: '14px', lineHeight: '20px' }}>info@ppsk.life</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Main Header */}
            <Box
                bgcolor="white"
                py={{ lg: '12px', md: "10px", sm: '8px', xs: '8px' }}
                sx={{ 
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    position: { xs: 'sticky', md: 'relative' },
                    top: 0,
                    zIndex: 1100,
                }}
            >
                <Container>
                    <Box
                        padding="3px"
                        top={0}
                        zIndex={100}
                        sx={{ 
                            display: 'flex', 
                            flexDirection: 'row',
                            gap: { lg: '40px', md: '40px', sm: '20px', xs: '12px' }, 
                            justifyContent: 'space-between',
                            alignItems: 'center' 
                        }}
                    >
                        {/* Logo */}
                        <img 
                            src="/images/Frame-1000008385.webp" 
                            alt="PPSK Logo" 
                            width="483"
                            height="120"
                            fetchpriority="high"
                            style={{ width: '100%', height: 'auto', maxWidth: isMobile ? '200px' : '284px', cursor: 'pointer' }} 
                            onClick={() => navigate("/")} 
                        />

                        {/* Desktop Navigation */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', gap: { lg: '20px', md: '16px', sm: '12px', xs: '10px' }, justifyContent: 'center', alignItems: 'center' }}>
                            {navItems.map((item) => (
                                <Typography 
                                    key={item.path}
                                    sx={{ ...navLinkStyle, color: item.isActive ? theme.palette.primary.main : 'inherit' }} 
                                    onClick={() => handleNavClick(item.path)}
                                >
                                    {item.label}
                                </Typography>
                            ))}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<AdminPanelSettingsOutlinedIcon />}
                                sx={{
                                    padding: '6px 14px',
                                    borderRadius: '10px',
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    fontSize: { lg: '14px', md: '14px', sm: '13px', xs: '12px' },
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                    },
                                }}
                                onClick={() => navigate('/admin')}
                            >
                                Admin Panel
                            </Button>
                            <Button
                                sx={{
                                    padding: '6px 20px',
                                    borderRadius: '8px',
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark || '#025e61',
                                    },
                                }}
                                onClick={() => navigate('/contact-us')}
                            >
                                Get in Touch
                            </Button>
                            </Box>
                        </Box>

                        {/* Mobile Hamburger Menu */}
                        <IconButton
                            sx={{ 
                                display: { xs: 'flex', md: 'none' },
                                color: theme.palette.primary.main,
                            }}
                            onClick={() => setMobileMenuOpen(true)}
                        >
                            <MenuIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                    </Box>
                </Container>
            </Box>

            {/* Mobile Drawer Menu */}
            <Drawer
                anchor="right"
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                PaperProps={{
                    sx: {
                        width: '80%',
                        maxWidth: 300,
                        backgroundColor: '#fff',
                    }
                }}
            >
                {/* Drawer Header */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '16px 20px',
                    borderBottom: '1px solid #e5e7eb',
                }}>
                    <Typography sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        Menu
                    </Typography>
                    <IconButton onClick={() => setMobileMenuOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Nav Items */}
                <List sx={{ padding: '8px' }}>
                    {navItems.map((item) => (
                        <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                                onClick={() => handleNavClick(item.path)}
                                sx={{
                                    borderRadius: '10px',
                                    backgroundColor: item.isActive ? `${theme.palette.primary.main}15` : 'transparent',
                                    '&:hover': {
                                        backgroundColor: item.isActive ? `${theme.palette.primary.main}20` : '#f5f5f5',
                                    },
                                }}
                            >
                                <ListItemText 
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: item.isActive ? 600 : 500,
                                        color: item.isActive ? theme.palette.primary.main : '#1e293b',
                                        fontSize: '15px',
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>

                {/* Buttons */}
                <Box sx={{ padding: '16px 20px', mt: 'auto' }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<AdminPanelSettingsOutlinedIcon />}
                        sx={{
                            padding: '12px 14px',
                            borderRadius: '10px',
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            fontSize: '14px',
                            textTransform: 'none',
                            mb: 1.5,
                        }}
                        onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                    >
                        Admin Panel
                    </Button>
                    <Button
                        fullWidth
                        sx={{
                            padding: '12px 20px',
                            borderRadius: '10px',
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            fontSize: '14px',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                            },
                        }}
                        onClick={() => { navigate('/contact-us'); setMobileMenuOpen(false); }}
                    >
                        Get in Touch
                    </Button>
                </Box>

                {/* Contact Info */}
                <Box sx={{ padding: '16px 20px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                    <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1 }}>Contact Us</Typography>
                    <Typography sx={{ fontSize: '13px', color: '#1e293b', mb: 0.5 }}>+91 98498 76476</Typography>
                    <Typography sx={{ fontSize: '13px', color: '#1e293b' }}>info@ppsk.life</Typography>
                </Box>
            </Drawer>
        </>
    )
}

export default Header